import { useState } from "react";
import { Container, Button } from "reactstrap";
import AppPageHeader from "../../../components/Headers/AppPageHeader";
import FormBuilderDrawer from "../../../components/FormBuilder/FormBuilderDrawer";
import { useAtomValue } from "jotai";
import { modulesAtom } from "../../../atoms/GlobalAtoms";
import BasicTableView from "../../supportal/CloudListView/BasicTableView";
import IconButton from "../../../elements/IconButton";
import usePublicForms from "../../../api/api-hooks/publicForms/usePublicForms";
import useDeletePublicForm from "../../../api/api-hooks/publicForms/useDeletePublicForm";
import { FormBuilder } from "../../../types/FormBuilderInterfaces";
import ModulePicker from "../../../components/Modules/ModulePicker";
import FormBuilderSuccessModal from "../../../components/FormBuilder/FormBuilderSuccessModal";
import { AppConfirm } from "../../../utils/PopupUtil";
import AppHelmet from "../../../components/AppComponents/AppHelmet";

const PublicForms = () => {
  const modules = useAtomValue(modulesAtom);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedModule, setSelectedModule] = useState<string | undefined>();
  const [selectedForm, setSelectedForm] = useState<FormBuilder | null>(null);
  const [filterModule, setFilterModule] = useState("");
  const [successFormID, setSuccessFormID] = useState("");
  const [share, setShare] = useState(false);

  const handleClose = () => {
    setSelectedForm(null);
    setOpenDrawer(false);
    setSelectedModule(undefined);
  };

  const { publicForms, publicFormsLoading } = usePublicForms();
  const { deletePublicForm, deletePublicFormError } = useDeletePublicForm();

  const handleEditPublicForm = (publicForm: FormBuilder) => {
    setSelectedForm(publicForm);
    setSelectedModule(publicForm?.ModuleAPIName);
    setOpenDrawer(true);
  };

  const handleDeletePublicForm = async (publicForm: FormBuilder) => {
    if (!publicForm || !publicForm._id) return;

    const result = await AppConfirm({
      title: "Confirm Deletion",
      text: "Are you sure you want to delete this form? All form configuration will be lost.",
    });

    if (!result.isConfirmed) return;

    deletePublicForm({ formID: publicForm._id });
  };

  const handleShareForm = (publicForm: FormBuilder) => {
    setSuccessFormID(publicForm.id as string);
    setShare(true);
  };

  const handleCloseSuccessModal = () => {
    setSuccessFormID("");
    setShare(false);
  };

  return (
    <>
      <AppHelmet title="Public Forms - GrowthCloud.com" />
      <Container fluid className="mt-4">
        <AppPageHeader
          title="Public Forms"
          description="Manage all cloud public forms."
          customContent={
            <div
              className="d-flex justify-content-between align-items-center"
              style={{ flexWrap: "wrap" }}
            >
              {!!modules?.length && (
                <div className={`mt-3 mt-sm-0 d-flex align-items-center mr-3`}>
                  <i
                    className="mdi mdi-database mr-1"
                    style={{ fontSize: 24 }}
                  />
                  <ModulePicker
                    value={filterModule}
                    onChange={(mod: string) => setFilterModule(mod)}
                    cleanable={true}
                    onClean={() => setFilterModule("")}
                  />
                </div>
              )}
              <div className="mt-3 mt-sm-0">
                <Button
                  className="ml-2"
                  color="primary"
                  onClick={() => setOpenDrawer(true)}
                >
                  Create
                </Button>
              </div>
            </div>
          }
        />
        <BasicTableView
          loading={publicFormsLoading}
          items={
            filterModule
              ? publicForms.filter((f) => f.ModuleAPIName === filterModule)
              : publicForms
          }
          title={"Form"}
          getItemTitle={(publicForm) => publicForm?.Header?.Name}
          getItemSubtitle={(publicForm) => publicForm?.ModuleAPIName}
          onClick={undefined}
          CustomRenderer={(publicForm) => {
            return (
              <div>
                <IconButton
                  icon="pencil"
                  tooltipText="Edit form"
                  onClick={() => handleEditPublicForm(publicForm)}
                />
                <IconButton
                  icon="share-variant"
                  tooltipText="Share form"
                  color="primary"
                  onClick={() => handleShareForm(publicForm)}
                />
                <IconButton
                  icon="close"
                  tooltipText="Delete form"
                  onClick={() => handleDeletePublicForm(publicForm)}
                  color="danger"
                />
              </div>
            );
          }}
          emptyMessage={"No public forms have been added."}
        />
        {openDrawer && (
          <FormBuilderDrawer
            open={openDrawer}
            onClose={handleClose}
            moduleAPIName={selectedModule}
            existingForm={selectedForm}
            setSuccessFormID={setSuccessFormID}
            setShare={setShare}
          />
        )}
        <FormBuilderSuccessModal
          open={!!successFormID}
          onClose={() => handleCloseSuccessModal()}
          formID={successFormID}
          share={share}
        />
      </Container>
    </>
  );
};

export default PublicForms;
